import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  Paper,
  Typography,
} from '@mui/material';
import registryService from '../services/registryService';
import useRegistrySyncRuns from '../hooks/useRegistrySyncRuns';
import { MAX_UPLOAD_BYTES } from '../utils/registrySourceConfig';
import { formatDate } from '../utils/dateUtils';
import { formatFileSize } from '../utils/fileUtils';
import { MONO_FONT } from '../constants/typography';
import RegistrySyncRunsTable, { RunStatusChip, formatCount } from './RegistrySyncRunsTable';

const PAGE_SIZE = 20;
const TOO_BIG = 'Il file supera 50 MB. Riduci le righe o dividilo.';
const ALREADY_RUNNING = 'Sincronizzazione già in corso: aspetta che finisca.';
const START_FAILED = 'Non riesco ad avviare la sincronizzazione: riprova tra poco.';

const whenText = (run) => {
  const started = formatDate(run.startedAt);
  if (!run.finishedAt || !run.startedAt) return started;
  const seconds = Math.max(0, Math.round((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000));
  return `${started} · ${seconds} s`;
};

const countersText = (run) => [
  `lette ${formatCount(run.rowsRead)}`,
  `nuove ${formatCount(run.rowsInserted)}`,
  `aggiornate ${formatCount(run.rowsUpdated)}`,
  ...(run.policy === 'full-replace' && run.rowsDeleted > 0 ? [`cancellate ${run.rowsDeleted}`] : []),
  `scartate ${formatCount(run.rowsRejected)}`,
].join(' · ');

// 409: il messaggio del backend distingue "già in corso" da "coda piena"; senza, il testo di ripiego.
const actionErrorOf = (err) => (err?.response?.status === 409
  ? { severity: 'warning', text: err.response.data?.message || ALREADY_RUNNING }
  : { severity: 'error', text: err?.response?.data?.message || START_FAILED });

/** Il cartellino dell'ultima esecuzione: quello che l'admin guarda quasi sempre. */
const LastRun = ({ run, running }) => {
  const tone = { FAILED: 'error.main', PARTIAL: 'warning.main' }[run.status];
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 3, rowGap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
          <RunStatusChip status={run.status} />
          {running && <CircularProgress size={14} aria-label="Sincronizzazione in corso" />}
          <Typography variant="body2" color="text.secondary">{whenText(run)}</Typography>
        </Box>
        <Typography variant="body2" sx={{ fontFamily: MONO_FONT, fontVariantNumeric: 'tabular-nums' }}>
          {countersText(run)}
        </Typography>
      </Box>
      {tone && run.message && (
        <Typography variant="body2" color={tone} sx={{ mt: 1 }}>{run.message}</Typography>
      )}
    </>
  );
};

const SyncActions = ({ registry, running, onStarted }) => {
  const isCsv = registry.sourceType === 'csv';
  const [file, setFile] = useState(null);
  const [tooBig, setTooBig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const submit = (call) => {
    setBusy(true);
    setActionError(null);
    Promise.resolve(call())
      .then(() => {
        setFile(null);
        onStarted();
      })
      .catch((err) => {
        setActionError(actionErrorOf(err));
        // 409: un'esecuzione e' gia' in corso; ricaricare la mostra e ne avvia il polling
        if (err?.response?.status === 409) onStarted();
      })
      .finally(() => setBusy(false));
  };

  const handleFile = (event) => {
    const chosen = event.target.files?.[0] || null;
    // azzera l'input: lo stesso file si puo' riscegliere dopo una correzione
    event.target.value = '';
    if (!chosen) return;
    const over = chosen.size > MAX_UPLOAD_BYTES;
    setTooBig(over);
    setFile(over ? null : chosen);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
        {isCsv ? (
          <>
            {file && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                {file.name} · {formatFileSize(file.size)}
              </Typography>
            )}
            <Button component="label" variant="outlined" disabled={running || busy}>
              Scegli file CSV
              <input type="file" accept=".csv,text/csv" hidden aria-label="File CSV" onChange={handleFile} />
            </Button>
            <Button
              variant="contained"
              disabled={!file || running || busy}
              onClick={() => submit(() => registryService.uploadCsv(registry.id, file))}
            >
              {running ? 'In esecuzione…' : 'Carica e applica'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            disabled={running || busy}
            onClick={() => submit(() => registryService.startSync(registry.id))}
          >
            {running ? 'In esecuzione…' : 'Sincronizza ora'}
          </Button>
        )}
      </Box>
      {tooBig && <FormHelperText error sx={{ textAlign: 'right' }}>{TOO_BIG}</FormHelperText>}
      {actionError && <Alert severity={actionError.severity} sx={{ mt: 1.5 }}>{actionError.text}</Alert>}
    </Box>
  );
};

const SyncPanelContent = ({ registry, canSync }) => {
  const {
    runs, totalElements, page, setPage, loading, error, reload, lastRun, running,
  } = useRegistrySyncRuns(registry, { size: PAGE_SIZE });
  const empty = !lastRun && runs.length === 0 && !loading && !error;
  const emptyText = registry.sourceType === 'csv'
    ? 'Nessuna esecuzione. Carica un file CSV per inserire le voci.'
    : 'Nessuna esecuzione. Sincronizza ora per caricare le voci.';

  return (
    <>
      <Paper variant="outlined" component="section" aria-label="Ultima esecuzione" sx={{ p: 2 }}>
        {lastRun && <LastRun run={lastRun} running={running} />}
        {empty && <Typography variant="body2" color="text.secondary">{emptyText}</Typography>}
        {canSync && <SyncActions registry={registry} running={running} onStarted={reload} />}
      </Paper>
      {!empty && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Esecuzioni</Typography>
          <RegistrySyncRunsTable
            runs={runs}
            totalElements={totalElements}
            page={page}
            onPageChange={setPage}
            size={PAGE_SIZE}
            loading={loading}
            error={error}
          />
        </>
      )}
    </>
  );
};

/** Pannello Sincronizzazione di un'anagrafica: ultima esecuzione, avvio o caricamento, storico. */
const RegistrySyncPanel = ({ open, registry, canSync, onClose }) => (
  <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Box component="span">Sincronizzazione · {registry?.name || registry?.code}</Box>
      {registry?.code && (
        <Chip size="small" variant="outlined" label={registry.code} sx={{ fontFamily: MONO_FONT, fontWeight: 500 }} />
      )}
    </DialogTitle>
    <DialogContent>
      {open && registry && <SyncPanelContent registry={registry} canSync={canSync} />}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Chiudi</Button>
    </DialogActions>
  </Dialog>
);

export default RegistrySyncPanel;
